import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  WalletSettingsResponse,
  PartnerWalletInfo,
  OwnerLedgerItem,
  WalletRechargeItem,
  WalletRechargesCounts,
  WalletReconciliationResponse,
  WalletListItem,
  WalletSummaryResponse,
} from '../../api/walletApi';

export interface WalletState {
  wallets: {
    items: WalletListItem[];
    total: number;
    limit: number;
    offset: number;
    min_balance_to_purchase: string;
    loading: boolean;
    error: string | null;
  };
  recharges: {
    items: WalletRechargeItem[];
    total: number;
    limit: number;
    offset: number;
    total_amount?: string;
    counts?: WalletRechargesCounts;
    loading: boolean;
    error: string | null;
  };
  summary: {
    data: WalletSummaryResponse | null;
    loading: boolean;
    error: string | null;
  };
  reconciliation: {
    data: WalletReconciliationResponse | null;
    loading: boolean;
    error: string | null;
  };
  settings: {
    data: WalletSettingsResponse | null;
    loading: boolean;
    saving: boolean;
    error: string | null;
  };
  partnerWallet: {
    data: PartnerWalletInfo | null;
    loading: boolean;
    error: string | null;
    ledger: {
      items: OwnerLedgerItem[];
      total: number;
      limit: number;
      offset: number;
      loading: boolean;
      error: string | null;
    };
  };
}

const initialState: WalletState = {
  wallets: {
    items: [],
    total: 0,
    limit: 50,
    offset: 0,
    min_balance_to_purchase: '50.00',
    loading: false,
    error: null,
  },
  recharges: {
    items: [],
    total: 0,
    limit: 50,
    offset: 0,
    total_amount: '0.00',
    counts: undefined,
    loading: false,
    error: null,
  },
  summary: {
    data: null,
    loading: false,
    error: null,
  },
  reconciliation: {
    data: null,
    loading: false,
    error: null,
  },
  settings: {
    data: null,
    loading: false,
    saving: false,
    error: null,
  },
  partnerWallet: {
    data: null,
    loading: false,
    error: null,
    ledger: {
      items: [],
      total: 0,
      limit: 50,
      offset: 0,
      loading: false,
      error: null,
    },
  },
};

const walletSlice = createSlice({
  name: 'wallet',
  initialState,
  reducers: {
    // Wallets List
    setWalletsLoading: (state, action: PayloadAction<boolean>) => {
      state.wallets.loading = action.payload;
    },
    setWalletsError: (state, action: PayloadAction<string | null>) => {
      state.wallets.error = action.payload;
    },
    setWalletsPage: (
      state,
      action: PayloadAction<{
        items: WalletListItem[];
        total: number;
        limit: number;
        offset: number;
        min_balance_to_purchase: string;
      }>
    ) => {
      const { items, total, limit, offset, min_balance_to_purchase } = action.payload;
      state.wallets.items = items;
      state.wallets.total = total;
      state.wallets.limit = limit;
      state.wallets.offset = offset;
      state.wallets.min_balance_to_purchase = min_balance_to_purchase;
      state.wallets.loading = false;
      state.wallets.error = null;
    },

    // Recharges List
    setRechargesLoading: (state, action: PayloadAction<boolean>) => {
      state.recharges.loading = action.payload;
    },
    setRechargesError: (state, action: PayloadAction<string | null>) => {
      state.recharges.error = action.payload;
    },
    setRechargesPage: (
      state,
      action: PayloadAction<{
        items: WalletRechargeItem[];
        total: number;
        limit: number;
        offset: number;
        total_amount?: string;
        counts?: WalletRechargesCounts;
      }>
    ) => {
      const { items, total, limit, offset, total_amount, counts } = action.payload;
      state.recharges.items = items;
      state.recharges.total = total;
      state.recharges.limit = limit;
      state.recharges.offset = offset;
      state.recharges.total_amount = total_amount;
      state.recharges.counts = counts;
      state.recharges.loading = false;
      state.recharges.error = null;
    },
    updateRechargeItem: (state, action: PayloadAction<WalletRechargeItem>) => {
      const idx = state.recharges.items.findIndex((r) => r.id === action.payload.id);
      if (idx !== -1) {
        state.recharges.items[idx] = action.payload;
      }
    },

    // Summary
    setSummaryLoading: (state, action: PayloadAction<boolean>) => {
      state.summary.loading = action.payload;
    },
    setSummaryError: (state, action: PayloadAction<string | null>) => {
      state.summary.error = action.payload;
    },
    setSummaryData: (state, action: PayloadAction<WalletSummaryResponse>) => {
      state.summary.data = action.payload;
      state.summary.loading = false;
      state.summary.error = null;
    },

    // Reconciliation Check
    setReconciliationLoading: (state, action: PayloadAction<boolean>) => {
      state.reconciliation.loading = action.payload;
    },
    setReconciliationError: (state, action: PayloadAction<string | null>) => {
      state.reconciliation.error = action.payload;
    },
    setReconciliationData: (state, action: PayloadAction<WalletReconciliationResponse>) => {
      state.reconciliation.data = action.payload;
      state.reconciliation.loading = false;
      state.reconciliation.error = null;
    },

    // Settings
    setSettingsLoading: (state, action: PayloadAction<boolean>) => {
      state.settings.loading = action.payload;
    },
    setSettingsSaving: (state, action: PayloadAction<boolean>) => {
      state.settings.saving = action.payload;
    },
    setSettingsError: (state, action: PayloadAction<string | null>) => {
      state.settings.error = action.payload;
    },
    setSettingsData: (state, action: PayloadAction<WalletSettingsResponse>) => {
      state.settings.data = action.payload;
      state.settings.loading = false;
      state.settings.saving = false;
      state.settings.error = null;
    },

    // Partner Wallet & Ledger
    setPartnerWalletLoading: (state, action: PayloadAction<boolean>) => {
      state.partnerWallet.loading = action.payload;
    },
    setPartnerWalletError: (state, action: PayloadAction<string | null>) => {
      state.partnerWallet.error = action.payload;
    },
    setPartnerWalletData: (state, action: PayloadAction<PartnerWalletInfo | null>) => {
      state.partnerWallet.data = action.payload;
      state.partnerWallet.loading = false;
      state.partnerWallet.error = null;
    },
    setPartnerLedgerLoading: (state, action: PayloadAction<boolean>) => {
      state.partnerWallet.ledger.loading = action.payload;
    },
    setPartnerLedgerError: (state, action: PayloadAction<string | null>) => {
      state.partnerWallet.ledger.error = action.payload;
    },
    setPartnerLedgerPage: (
      state,
      action: PayloadAction<{
        items: OwnerLedgerItem[];
        total: number;
        limit: number;
        offset: number;
      }>
    ) => {
      const { items, total, limit, offset } = action.payload;
      state.partnerWallet.ledger.items = items;
      state.partnerWallet.ledger.total = total;
      state.partnerWallet.ledger.limit = limit;
      state.partnerWallet.ledger.offset = offset;
      state.partnerWallet.ledger.loading = false;
      state.partnerWallet.ledger.error = null;
    },
  },
});

export const {
  setWalletsLoading,
  setWalletsError,
  setWalletsPage,
  setRechargesLoading,
  setRechargesError,
  setRechargesPage,
  updateRechargeItem,
  setSummaryLoading,
  setSummaryError,
  setSummaryData,
  setReconciliationLoading,
  setReconciliationError,
  setReconciliationData,
  setSettingsLoading,
  setSettingsSaving,
  setSettingsError,
  setSettingsData,
  setPartnerWalletLoading,
  setPartnerWalletError,
  setPartnerWalletData,
  setPartnerLedgerLoading,
  setPartnerLedgerError,
  setPartnerLedgerPage,
} = walletSlice.actions;

export default walletSlice.reducer;
